import { CommonModule } from "@angular/common";
import { Component, ElementRef, EventEmitter, Output, signal, ViewChild, WritableSignal } from "@angular/core";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { TranslateModule } from "@ngx-translate/core";
import { IExecuteProcedureRequest, IExecuteProcedureResult, IGetCashMovementsSummaryRequest, IGetCashMovementsSummaryResponse } from "@dert-services";
import { BehaviorSubject } from "rxjs";
import { DbSchemeEnum } from "src/app/enums/db-scheme.enum";
import { ProcedureNameEnum } from "src/app/enums/procedure-name.enum";
import { SharedModule } from "src/app/shared.module";
import { CreateFinancialTransactionResponse } from "src/app/services/model/create-financial-transaction-response.model";
import { CreateFinancialTransactionRequest, FinancialTransactionType, TransactionTypeCodes } from "src/app/services/model/create-financial-transaction.model";
import { ITaxItem } from "src/app/services/model/sp-procedures-params.model";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { DropdownComponent } from "../../../components/ui-components/dropdown/dropdown.component";
import { DropdownOption } from "../../../components/ui-components/dropdown/dropdown.model";
import { FormContainerComponent } from "../../../components/ui-components/forms/form-container/form-container.component";
import { FormInputComponent } from "../../../components/ui-components/forms/form-input/form-input.component";
import { FormInputType } from "../../../components/ui-components/forms/form-input/form-input.model";
import { PopUpInformalType } from "../../../components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { FinancialDocumentBase } from "../financial-component-base";

interface ConfirmedTransaction {
    name: string;
    reason: string;
    value: number;
    vat: number;
}

@Component({
  selector: "app-withdraw-with-document",
  templateUrl: "./withdraw-with-document.component.html",
  styleUrls: ["./withdraw-with-document.component.scss"],
  imports: [
    ReactiveFormsModule,
    FormContainerComponent,
    FormInputComponent,
    TranslateModule,
    FontAwesomeModule,
    CommonModule,
    DropdownComponent,
    SharedModule,
  ],
  standalone: true,
})

export class WithdrawWithDocumentComponent extends FinancialDocumentBase {

    @ViewChild("scrollContainer")
    private scrollContainer?: ElementRef<HTMLElement>;

    @ViewChild("formContainer")
    private formContainer!: FormContainerComponent;

    private shouldScroll = false;
    selectedRowIndex: number | null = null;

    isConfirmed: WritableSignal<boolean> = signal(false);

    form = new FormGroup({
        rows: new FormArray([this.createRow()], Validators.required),
        checkSum: new FormControl<number | null>({ value: 0, disabled: true }, [Validators.min(1)]),
        note: new FormControl<string>(""),
    });

    vatOptions: BehaviorSubject<DropdownOption[]> = new BehaviorSubject<DropdownOption[]>([]);

    FormInputType = FormInputType;
    finDocType = FinancialTransactionType.PickupFromTill;
    availableAmount: number | null = null;
    amountInsufficient: boolean = false;
    printingRowIndex: number | null = null;

    confirmedTransactions: WritableSignal<ConfirmedTransaction[]> = signal([]);
    createdTransactionResponses: WritableSignal<CreateFinancialTransactionResponse[]> = signal([]);

    @Output() onClose = new EventEmitter<void>();

    async ngOnInit() {
        await this.loadData();
        
        // Set first reason option as default if not already set
        if (this.reasonOptions.length > 0 && !this.form.controls.rows.getRawValue()[0]?.reason) {
            const firstReasonCode = this.reasonOptions[0].value;
            this.rows.at(0).get('reason')?.setValue(firstReasonCode, { emitEvent: false });
        }
        
        const taxItemsRequest = await this._sharedService.getTaxItems();

        const res: IExecuteProcedureResult<any> =
        await this.sharedApiService.executeProcedure(taxItemsRequest);
        
        if (res && res.data && res.data.length > 0) {
            this.vatOptions.next(this.buildVatOptions(res.data));
        }
        
        this.setupValueChangeListeners();

        // Load available cash for the Cash payment type
        const paymentTypes = await this._sharedService.getPaymentTypes();
        const cashPaymentType = paymentTypes.find(pt => pt.TenderType === TenderType.Cash);
        if (cashPaymentType) {
            await this.loadAvailableAmount(cashPaymentType.Name);
        }

        this.recalculateCheckSum();
    }

    private async loadAvailableAmount(paymentName: string): Promise<void> {
        try {
            const jsonData = {
                CurrencyCode: this.loginService.user?.CurrencyCode,
                PaymentName: paymentName,
                ReturnToResult: 0,
            } as IGetCashMovementsSummaryRequest;

            const request: IExecuteProcedureRequest<IGetCashMovementsSummaryRequest> = {
                DbScheme: DbSchemeEnum.Dert,
                ProcedureName: ProcedureNameEnum.GetCashMovementsSummary,
                Parameters: jsonData,
            };

            const result: IExecuteProcedureResult<IGetCashMovementsSummaryResponse> = await this.sharedApiService.executeProcedure(request);

            if (result?.data?.TotalMovement !== undefined && result.data.TotalMovement !== null) {
                this.availableAmount = result.data.TotalMovement;
            } else {
                this.availableAmount = null;
            }
        } catch (error) {
            console.error('Failed to load available cash amount', error);
            this.availableAmount = null;
        } finally {
            this.updateAmountInsufficient();
        }
    }

    private updateAmountInsufficient(): void {
        const value = Math.abs(this.checkSumValue);
        this.amountInsufficient = this.availableAmount !== null && value > this.availableAmount;
    }

    ngAfterViewChecked() {
        if (this.shouldScroll) {
            this.scrollToBottom();
            this.shouldScroll = false;
        }
    }

    handleSubmit(): void {
        if (this.isConfirmed()) {
            this.close();
            return;
        }

        void this.createTransaction();
    }

    private createRow(): FormGroup {
        return new FormGroup({
            name: new FormControl<string>("", Validators.required),
            reason: new FormControl<string>("", Validators.required),
            value: new FormControl<number | null>(null, Validators.required),
            vat: new FormControl<number | null>(null, Validators.required),
        });
    }

    private buildVatOptions(items: ITaxItem[]): DropdownOption[] {
        const uniqueOptions = new Map<number, DropdownOption>();

        [...items]
            .sort((a, b) => Number(b.Rate) - Number(a.Rate))
            .forEach((item: ITaxItem) => {
                const rate = Number(item.Rate);

                if (!uniqueOptions.has(rate)) {
                    uniqueOptions.set(rate, {
                        label: `${item.DisplayValue}`,
                        value: rate,
                    });
                }
            });

        return Array.from(uniqueOptions.values());
    }

    get rows(): FormArray {
        return this.form.get("rows") as FormArray;
    }

    get checkSumValue(): number {
        return Number(this.form.get("checkSum")?.value ?? 0);
    }

    get isSubmitDisabled(): boolean {
        return this.form.invalid || this.amountInsufficient || this.checkSumValue <= 0;
    }

    addRow() {
        this.rows.push(this.createRow());
        this.shouldScroll = true;
        this.selectedRowIndex = this.rows.length - 1;
        this.setupRowValueListener(this.rows.length - 1);

        // Set first reason option as default if not already set
        if (this.reasonOptions.length > 0 && !this.rows.at(this.selectedRowIndex).get('reason')?.value) {
            const firstReasonCode = this.reasonOptions[0].value;
            this.rows.at(this.selectedRowIndex).get('reason')?.setValue(firstReasonCode, { emitEvent: false });
        }
    }

    removeRow(index?: number) {
        const targetIndex = index ?? this.selectedRowIndex ?? 0;

        if (this.rows.length <= 1 || targetIndex < 0 || targetIndex >= this.rows.length) {
            return;
        }

        this.rows.removeAt(targetIndex);
        this.selectedRowIndex = this.rows.length ? Math.min(targetIndex, this.rows.length - 1) : null;
        this.recalculateCheckSum();
    }

    selectRow(index: number): void {
        this.selectedRowIndex = index;
    }

    private setupValueChangeListeners(): void {
        this.rows.controls.forEach((_, index) => {
            this.setupRowValueListener(index);
        });
    }

    private setupRowValueListener(index: number): void {
        const row = this.rows.at(index);
        row.get('reason')?.valueChanges.subscribe(() => {
            this.form.updateValueAndValidity({ emitEvent: false });
        });
        row.get('value')?.valueChanges.subscribe(() => {
            this.recalculateCheckSum();
        });
    }

    private recalculateCheckSum(): void {
        let sum = 0;
        this.rows.controls.forEach(row => {
            const value = Number(row.get('value')?.value);
            if (!isNaN(value)) {
                sum += value;
            }
        });
        this.form.get('checkSum')?.setValue(sum);
        this.updateAmountInsufficient();
    }

    private scrollToBottom(): void {
        const container = this.scrollContainer?.nativeElement;
        if (!container) {
            return;
        }

        container.scrollTop = container.scrollHeight;
    }

    public async createTransaction() {
        if (this.isSubmitDisabled) {
            this.form.markAllAsTouched();
            return;
        }
        this.spinner.show();

        const requests = this.buildRequests();
        const responses = await this.posDataService.createMultiFinancialTransactions(requests);

        if (!responses || responses.length === 0) {
            this.spinner.hide();
            this.popUpDialogInformalService.confirm({
                message: this.translate.instant("webPos.Errors.UnexpectedError"),
                title: this.translate.instant("webPos.Errors.Error"),
                type: PopUpInformalType.Error,
            });
            return;
        }

        this.spinner.hide();

        this.isConfirmed.set(true);
        this.shouldScroll = false;
        this.createdTransactionResponses.set(responses);

        this.formContainer.confirmText = "webPos.Close";
        this.formContainer.showCancelButton = false;

        this.confirmedTransactions.set(this.mapConfirmedTransactions(responses));
    }

    public async printTransaction(index: number): Promise<void> {
        const response = this.createdTransactionResponses()[index];

        if (!response || this.printingRowIndex !== null) {
            return;
        }

        this.printingRowIndex = index;
        this.spinner.show();

        try {
            await this.printCreatedTransactions([response]);
        } catch (error) {
            console.error('Failed to print created transaction', error);
            await this.popUpDialogInformalService.confirm({
                message: this.translate.instant("webPos.Errors.UnexpectedError"),
                title: this.translate.instant("webPos.Errors.Error"),
                type: PopUpInformalType.Error,
            });
        } finally {
            this.spinner.hide();
            this.printingRowIndex = null;
        }
    }

    private buildRequests(): CreateFinancialTransactionRequest[] {
        const currencyCode = this.loginService.user.CurrencyCode;
        const note = this.form.get("note")?.value ?? '';
        const checkSum = this.checkSumValue;

        return this.rows.getRawValue().map((row: { name: string; reason: string; value: number; vat: number; }) => {
            const normalizedRow = {
                ...row,
                value: Number(row.value) || 0,
                vat: Number(row.vat) || 0,
            };

            return <CreateFinancialTransactionRequest>{
                TransactionTypeCode: TransactionTypeCodes.PickupFromTillDocument,
                FinancialTransactionType: this.finDocType,
                TotalPrice: normalizedRow.value,
                ReasonCode: normalizedRow.reason,
                ReasonText: this.reasonOptions.find(reason => reason.value === normalizedRow.reason)?.label ?? normalizedRow.reason,
                CurrencyISOCode: currencyCode,
                TenderType: TenderType.Cash,
                TenderSubType: '',
                Note: note,
                Payment: {
                    PaymentAmount: normalizedRow.value,
                    CurrencyISOCode: currencyCode,
                    TenderType: TenderType.Cash,
                },
                Attributes: {
                    FinancialTransactionRows: JSON.stringify([normalizedRow]),
                    ItemName: normalizedRow.name,
                    VAT: normalizedRow.vat.toString(),
                    Note: note,
                    CheckSum: checkSum.toString(),
                },
                AdditionalJsonData: JSON.stringify({
                    Note: note,
                    Row: normalizedRow,
                    CheckSum: checkSum,
                }),
                DenominationItems: [],
            };
        });
    }

    private mapConfirmedTransactions(responses: CreateFinancialTransactionResponse[]): ConfirmedTransaction[] {
        return responses.map((response) => {
            const headerRecord = response.records.find((record) => record.name === "Header") ?? response.records[0];
            const headerComponents = headerRecord?.components ?? [];
            const movementComponent = this.findComponent<{ movementReasonText?: string; totalPrice?: number }>(headerComponents, ["movementReasonText", "totalPrice"]);
            const attributesComponent = this.findComponent<{ attributes?: Record<string, string> }>(headerComponents, ["attributes"]);
            const parsedRow = this.parseFinancialTransactionRows(attributesComponent?.attributes?.FinancialTransactionRows)?.[0] ?? {};

            return {
                name: this.asString(parsedRow.name) ?? attributesComponent?.attributes?.ItemName ?? "",
                reason: this.asString(parsedRow.reason)
                    ?? movementComponent?.movementReasonText
                    ?? response.properties[1]?.transactionType
                    ?? response.properties[1]?.nameTransactionMap
                    ?? "",
                value: this.asNumber(parsedRow.value) ?? movementComponent?.totalPrice ?? 0,
                vat: this.asNumber(parsedRow.vat) ?? this.asNumber(attributesComponent?.attributes?.VAT) ?? 0,
            };
        });
    }

    private parseFinancialTransactionRows(rawRows?: string): Array<{ name?: unknown; reason?: unknown; value?: unknown; vat?: unknown }> | null {
        if (!rawRows) {
            return null;
        }

        try {
            const parsed = JSON.parse(rawRows);
            return Array.isArray(parsed) ? parsed : null;
        } catch {
            return null;
        }
    }

    private findComponent<T>(components: unknown[], keys: string[]): T | undefined {
        return components.find((component) => this.hasAnyKey(component, keys)) as T | undefined;
    }

    private hasAnyKey(value: unknown, keys: string[]): value is Record<string, unknown> {
        return typeof value === "object" && value !== null && keys.some((key) => key in value);
    }

    private asString(value: unknown): string | undefined {
        return typeof value === "string" ? value : value === null || value === undefined ? undefined : String(value);
    }

    private asNumber(value: unknown): number | undefined {
        if (typeof value === "number" && Number.isFinite(value)) {
            return value;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    }

    private async printCreatedTransactions(responses: CreateFinancialTransactionResponse[]): Promise<void> {
        for (const response of responses) {
            const components = response?.records?.find(r => r.name === 'Header')?.components;

            if (!components || components.length === 0) {
                continue;
            }

            const trxUID = components.find((component): component is { trxUID: string } => this.hasAnyKey(component, ["trxUID"]))?.trxUID;

            if (!trxUID) {
                continue;
            }

            const headerProcessingResult = await this.headerProcessingService.monitorHeader(trxUID);
            const headerIdForPrint = this.headerProcessingService.getHeaderIdFromResult(headerProcessingResult);

            if (headerIdForPrint) {
                await this.reportExportService.downloadPdf(
                    undefined,
                    [{ key: 'HeaderID', value: headerIdForPrint.toString() }],
                    true
                );
            }
        }
    }
}
